import { useEffect } from 'react';

// Helper to inject schema into document head with unique identification
const useSchemaMarkup = (schema, schemaId = null) => {
  useEffect(() => {
    if (!schema) return;
    
    // Generate a unique ID for the schema
    const id = schemaId || `schema-${schema['@type']}-${Date.now()}`;
    
    // Remove any existing schema with the same type (for page-specific schemas like FAQPage)
    if (schema['@type'] === 'FAQPage') {
      const existingFaqSchemas = document.querySelectorAll('script[data-schema-type="FAQPage"]');
      existingFaqSchemas.forEach(el => el.remove());
    }
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema-id', id);
    script.setAttribute('data-schema-type', schema['@type']);
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    
    return () => {
      const el = document.querySelector(`script[data-schema-id="${id}"]`);
      if (el) el.remove();
    };
  }, [schema, schemaId]);
};

// Organization Schema
export const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Thrive Business Coaching",
    "alternateName": "Treat Your Business",
    "url": "https://thrive-businesscoaching.com",
    "logo": "https://thrive-businesscoaching.com/logo.png",
    "description": "Business growth, marketing, systems and leadership for UK clinic owners. Helping physiotherapists, osteopaths, and health professionals build thriving practices.",
    "foundingDate": "2020",
    "founder": {
      "@type": "Person",
      "name": "Katie Bell"
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "GB"
    },
    "sameAs": [
      "https://www.youtube.com/channel/UCdDSTgai-nMLB_dW97qaj6g",
      "https://thrive-businesscoaching.com"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "url": "https://api.leadconnectorhq.com/widget/survey/Qu9hIDFB8ZGNEPPjfJ5y"
    }
  };

  useSchemaMarkup(schema);
  return null;
};

// Person Schema for Katie Bell
export const PersonSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Katie Bell",
    "jobTitle": "Physiotherapist, Clinic Owner & Business Coach",
    "description": "Katie Bell is a practising physiotherapist, successful clinic owner, #1 bestselling author of Thriving, and founder of Thrive Business Coaching. She helps UK clinic owners grow profitable practices.",
    "url": "https://thrive-businesscoaching.com",
    "sameAs": [
      "https://www.youtube.com/@thrivebizcoach",
      "https://www.instagram.com/thrivebusinesscoaching/",
      "https://www.facebook.com/thrivebusinesscoaching",
      "https://www.linkedin.com/company/thrive-business-coaching/",
      "https://www.linkedin.com/in/kaborbell/"
    ],
    "worksFor": {
      "@type": "Organization",
      "name": "Thrive Business Coaching"
    },
    "knowsAbout": [
      "Physiotherapy",
      "Clinic Management",
      "Business Coaching",
      "Healthcare Business Growth",
      "Team Building",
      "Clinic Marketing"
    ]
  };

  useSchemaMarkup(schema);
  return null;
};

// WebSite Schema
export const WebSiteSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Thrive Business Coaching",
    "url": "https://thrive-businesscoaching.com",
    "description": "Podcast and content hub for UK clinic owners",
    "publisher": {
      "@type": "Organization",
      "name": "Thrive Business Coaching"
    }
  };

  useSchemaMarkup(schema);
  return null;
};

// Podcast Episode Schema
export const PodcastEpisodeSchema = ({ episode, topics }) => {
  const topicName = topics?.find(t => t.id === episode?.topic_id)?.name || '';

  const schema = episode ? {
    "@context": "https://schema.org",
    "@type": "PodcastEpisode",
    "name": episode.title,
    "description": episode.description?.substring(0, 500),
    "url": `https://thrive-businesscoaching.com/podcast/${episode.slug}`,
    "datePublished": episode.publish_date,
    "duration": episode.duration ? `PT${Math.floor(episode.duration / 60)}M${episode.duration % 60}S` : undefined,
    "associatedMedia": {
      "@type": "AudioObject",
      "contentUrl": episode.audio_url
    },
    "partOfSeries": {
      "@type": "PodcastSeries",
      "name": "Treat Your Business Podcast",
      "url": "https://thrive-businesscoaching.com/listen"
    },
    "author": {
      "@type": "Person",
      "name": "Katie Bell"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Thrive Business Coaching"
    },
    "keywords": topicName
  } : null;

  useSchemaMarkup(schema);
  return null;
};

// FAQ Schema
export const FAQSchema = ({ faqs }) => {
  const schema = faqs && faqs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  useSchemaMarkup(schema);
  return null;
};

// Video Schema
export const VideoSchema = ({ video }) => {
  const schema = video ? {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": video.title,
    "description": video.description,
    "thumbnailUrl": video.thumbnail_url,
    "uploadDate": video.publish_date,
    "duration": video.duration ? `PT${Math.floor(video.duration / 60)}M${video.duration % 60}S` : undefined,
    "embedUrl": `https://www.youtube.com/embed/${video.youtube_id}`,
    "contentUrl": `https://www.youtube.com/watch?v=${video.youtube_id}`,
    "author": {
      "@type": "Person",
      "name": "Katie Bell"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Thrive Business Coaching"
    }
  } : null;

  useSchemaMarkup(schema);
  return null;
};

// Breadcrumb Schema
export const BreadcrumbSchema = ({ items }) => {
  const schema = items && items.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  } : null;

  useSchemaMarkup(schema);
  return null;
};

// Speakable Schema for Voice Search Optimization
export const SpeakableSchema = ({ 
  url, 
  headline, 
  summary,
  cssSelectors = ['.speakable-headline', '.speakable-summary']
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "url": url,
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": cssSelectors
    },
    "name": headline,
    "description": summary
  };

  useSchemaMarkup(schema);
  return null;
};

// Article Schema for Blog Posts
export const ArticleSchema = ({ article }) => {
  const imageUrl = article?.image_url || article?.featured_image;
  const schema = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.description?.substring(0, 160),
    "image": imageUrl ? {
      "@type": "ImageObject",
      "contentUrl": imageUrl,
      "url": imageUrl,
      "creator": { "@type": "Organization", "name": "Thrive Business Coaching" },
      "creditText": "Thrive Business Coaching",
      "copyrightNotice": "© Thrive Business Coaching Ltd",
      "license": "https://thrive-businesscoaching.com/terms",
      "acquireLicensePage": "https://thrive-businesscoaching.com/terms"
    } : undefined,
    "datePublished": article.publish_date,
    "dateModified": article.updated_at || article.publish_date,
    "author": {
      "@type": "Person",
      "name": "Katie Bell"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Thrive Business Coaching",
      "logo": {
        "@type": "ImageObject",
        "url": "https://thrive-businesscoaching.com/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://thrive-businesscoaching.com/learn/${article.slug}`
    }
  } : null;

  useSchemaMarkup(schema);
  return null;
};

// Page Meta Tags - uses native document methods for React 19 compatibility
export const PageMeta = ({ 
  title, 
  description, 
  url, 
  image,
  type = 'website'
}) => {
  const fullTitle = title
    ? (title.includes('|') || /Thrive|Treat Your Business/i.test(title) ? title : `${title} | Thrive`)
    : 'Specialist Business Coaching for UK Clinic Owners | Thrive';
  const defaultDescription = 'Business growth, marketing, systems and leadership for UK clinic owners. Helping physiotherapists, osteopaths, and health professionals build thriving practices.';
  const defaultImage = 'https://thrive-businesscoaching.com/og-image.jpg';
  const metaDescription = description || defaultDescription;
  const metaUrl = url || 'https://thrive-businesscoaching.com';
  const metaImage = image || defaultImage;

  useEffect(() => {
    // Set document title
    document.title = fullTitle;
    
    // Helper to set or create meta tag
    const setMetaTag = (name, content, property = false) => {
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    // Set canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', metaUrl);
    
    // Set meta tags
    setMetaTag('description', metaDescription);
    
    // Open Graph
    setMetaTag('og:title', fullTitle, true);
    setMetaTag('og:description', metaDescription, true);
    setMetaTag('og:type', type, true);
    setMetaTag('og:url', metaUrl, true);
    setMetaTag('og:image', metaImage, true);
    setMetaTag('og:site_name', 'Thrive Business Coaching', true);
    setMetaTag('og:locale', 'en_GB', true);
    
    // Twitter Card
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', fullTitle);
    setMetaTag('twitter:description', metaDescription);
    setMetaTag('twitter:image', metaImage);
    
  }, [fullTitle, metaDescription, metaUrl, metaImage, type]);

  return null;
};

// ItemList Schema for Collection Pages (enables carousel rich results)
export const ItemListSchema = ({ items, listType = 'ItemList', name, description }) => {
  const schema = items && items.length > 0 ? {
    "@context": "https://schema.org",
    "@type": listType,
    "name": name,
    "description": description,
    "numberOfItems": items.length,
    "itemListElement": items.slice(0, 10).map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": item.type || "Thing",
        "name": item.name,
        "url": item.url,
        "image": item.image,
        "description": item.description?.substring(0, 160)
      }
    }))
  } : null;

  useSchemaMarkup(schema);
  return null;
};

// VideoObject ItemList for Watch page
export const VideoListSchema = ({ videos, totalCount }) => {
  const schema = videos && videos.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Physiotherapy Business Videos for UK Clinic Owners",
    "description": "Video content for UK clinic owners covering how to grow a physiotherapy business, scale your clinic, increase clinic revenue, and build a profitable private practice.",
    "numberOfItems": totalCount || videos.length,
    "itemListElement": videos.slice(0, 10).map((video, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "VideoObject",
        "name": video.title,
        "description": video.description?.substring(0, 160),
        "thumbnailUrl": video.thumbnail_url,
        "uploadDate": video.publish_date,
        "embedUrl": `https://www.youtube.com/embed/${video.youtube_id}`,
        "contentUrl": `https://www.youtube.com/watch?v=${video.youtube_id}`,
        "duration": video.duration ? `PT${Math.floor(video.duration / 60)}M${video.duration % 60}S` : undefined
      }
    }))
  } : null;

  useSchemaMarkup(schema);
  return null;
};

// Podcast Episode ItemList for Listen page
export const PodcastListSchema = ({ episodes, totalCount }) => {
  const schema = episodes && episodes.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Treat Your Business Podcast - The Clinic Business Podcast for UK Clinic Owners",
    "description": "Healthcare business podcast for UK clinic owners, physiotherapy business owners, osteopath business owners, and chiropractor business owners.",
    "numberOfItems": totalCount || episodes.length,
    "itemListElement": episodes.slice(0, 10).map((episode, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "PodcastEpisode",
        "name": episode.title,
        "description": episode.description?.substring(0, 160),
        "url": `https://thrive-businesscoaching.com/podcast/${episode.slug}`,
        "datePublished": episode.publish_date,
        "duration": episode.duration ? `PT${Math.floor(episode.duration / 60)}M${episode.duration % 60}S` : undefined
      }
    }))
  } : null;

  useSchemaMarkup(schema);
  return null;
};

// Blog/Article ItemList for Learn page
export const ArticleListSchema = ({ articles, totalCount }) => {
  const schema = articles && articles.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Clinic Business Articles & Guides for UK Healthcare Practice Owners",
    "description": "Comprehensive guides and articles helping UK clinic owners grow their physiotherapy practice, increase clinic revenue, and build profitable private practices.",
    "numberOfItems": totalCount || articles.length,
    "itemListElement": articles.slice(0, 10).map((article, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Article",
        "headline": article.title,
        "description": article.description?.substring(0, 160),
        "url": `https://thrive-businesscoaching.com/learn/${article.slug}`,
        "image": article.featured_image || article.image_url,
        "datePublished": article.publish_date || article.created_at
      }
    }))
  } : null;

  useSchemaMarkup(schema);
  return null;
};

// Book Schema for Katie's Book
export const BookSchema = ({ book }) => {
  const schema = book ? {
    "@context": "https://schema.org",
    "@type": "Book",
    "name": book.title,
    "author": {
      "@type": "Person",
      "name": "Katie Bell"
    },
    "description": book.description,
    "image": book.image,
    "url": book.url,
    "publisher": {
      "@type": "Organization",
      "name": "Thrive Business Coaching"
    },
    "inLanguage": "en-GB",
    "genre": ["Business", "Healthcare Business", "Self-Help"],
    "audience": {
      "@type": "Audience",
      "audienceType": "UK Clinic Owners, Physiotherapists, Osteopaths, Chiropractors"
    }
  } : null;

  useSchemaMarkup(schema);
  return null;
};

// Course/Program Schema
export const CourseSchema = ({ course }) => {
  const schema = course ? {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": course.title,
    "description": course.description,
    "provider": {
      "@type": "Organization",
      "name": "Thrive Business Coaching",
      "sameAs": "https://thrive-businesscoaching.com"
    },
    "instructor": {
      "@type": "Person",
      "name": "Katie Bell"
    },
    "educationalLevel": "Professional",
    "audience": {
      "@type": "Audience",
      "audienceType": "UK Clinic Owners, Physiotherapy Business Owners, Healthcare Professionals"
    },
    "inLanguage": "en-GB",
    "hasCourseInstance": course.instances?.map(instance => ({
      "@type": "CourseInstance",
      "courseMode": instance.mode || "Online",
      "name": instance.name
    }))
  } : null;

  useSchemaMarkup(schema);
  return null;
};

// LocalBusiness Schema for UK targeting
export const LocalBusinessSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Thrive Business Coaching - Business Coaching for UK Clinic Owners",
    "description": "Business coaching services for UK clinic owners including physiotherapy business owners, osteopath business owners, chiropractor business owners, and healthcare professionals looking to grow their private practice.",
    "url": "https://thrive-businesscoaching.com",
    "logo": "https://thrive-businesscoaching.com/logo192.png",
    "image": "https://thrive-businesscoaching.com/katie-bell.jpg",
    "priceRange": "££",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "GB",
      "addressRegion": "United Kingdom"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "addressCountry": "GB"
    },
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    },
    "serviceType": ["Business Coaching", "Healthcare Business Consulting", "Clinic Growth Strategy"],
    "founder": {
      "@type": "Person",
      "name": "Katie Bell"
    },
    "sameAs": [
      "https://www.youtube.com/@thaborbell",
      "https://www.instagram.com/kaborbell/",
      "https://www.linkedin.com/in/kaborbell/"
    ]
  };

  useSchemaMarkup(schema);
  return null;
};

// Aggregate Rating Schema for testimonials
export const AggregateRatingSchema = ({ rating, reviewCount, itemName }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": itemName || "Thrive Business Coaching Programs",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": rating || "5",
      "bestRating": "5",
      "worstRating": "1",
      "ratingCount": reviewCount || "500"
    }
  };

  useSchemaMarkup(schema);
  return null;
};

// Service Schema
export const ServiceSchema = ({ services }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Business Coaching for UK Clinic Owners",
    "provider": {
      "@type": "Organization",
      "name": "Thrive Business Coaching"
    },
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    },
    "description": "Business coaching and growth programs for UK clinic owners, physiotherapy business owners, osteopath business owners, and chiropractor business owners.",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Business Coaching Programs",
      "itemListElement": services?.map(service => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": service.name,
          "description": service.description
        }
      }))
    }
  };

  useSchemaMarkup(schema);
  return null;
};

export default {
  OrganizationSchema,
  PersonSchema,
  WebSiteSchema,
  PodcastEpisodeSchema,
  FAQSchema,
  VideoSchema,
  BreadcrumbSchema,
  SpeakableSchema,
  ArticleSchema,
  PageMeta,
  ItemListSchema,
  VideoListSchema,
  PodcastListSchema,
  ArticleListSchema,
  BookSchema,
  CourseSchema,
  LocalBusinessSchema,
  AggregateRatingSchema,
  ServiceSchema
};
